import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    TextField,
    Typography,
} from "@mui/material"
import {
    CheckBoxOutlinedBlankIcon,
    CheckBoxOutlinedIcon,
    CloseIcon,
    ExpandAllIcon,
    SmallErrorIcon,
} from "@tc/ui-dfe/icons"
import type { ReasonCategoryOption, ReasonDialogProps } from "./types"

const checkboxIcon = <CheckBoxOutlinedBlankIcon fontSize="small" fill={"info"} />
const checkedCheckboxIcon = <CheckBoxOutlinedIcon fontSize="small" />


export const FormErrorPrompt = ({ message }: { message?: string }) => {
    if (!message) {
        return null
    }

    return (
        <Stack direction="row" alignItems="center" gap={0.5}>
            <SmallErrorIcon
                sx={{
                    width: 18,
                    fill: (theme) => theme.palette.error.main,
                }}
            />
            <Typography variant="body2" color="error">
                {message || " "}
            </Typography>
        </Stack>
    )
}

export const ReasonDialog = (props: ReasonDialogProps) => {
    const {
        title,
        message,
        confirmLabel = "Confirm",
        cancelLabel,
        cancelProps,
        confirmProps,
        open,
        placeholder,
        cancelDialog,
        handleConfirm,
        error,
        setError,
        reason,
        setReason,
        reasonOptions,
        reasonSelectPlaceholder,
        selectedReasons = [],
        setSelectedReasons,
        reasonsError,
        setReasonsError,
        required,
        reasonsRequired,
        maxLength,
    } = props

    const hasReasonSelect = Array.isArray(reasonOptions) && reasonOptions.length > 0

    const selectedOptions: ReasonCategoryOption[] = hasReasonSelect
        ? reasonOptions.filter((option) => selectedReasons.includes(option.value))
        : []

    // Confirm-button enablement depends on which path is active:
    // - New/structured path (reason-category select present, i.e. feature flags
    //   ON): keep the field-based disabling so Confirm stays disabled until the
    //   required reason category is selected (and the note is within maxLength).
    // - Legacy path (no reason-category select, i.e. a flag is OFF): never
    //   disable Confirm. Any required-field enforcement happens on click via
    //   handleConfirm, which surfaces the "This field is required" message.
    const trimmedReason = reason?.trim() ?? ""
    const isReasonsInvalid = !!reasonsRequired && selectedReasons.length === 0
    const isNotesRequiredInvalid =
        !hasReasonSelect && !!required && trimmedReason.length === 0
    const isNotesTooLong = !!maxLength && (reason?.length ?? 0) > maxLength
    const isConfirmDisabled = hasReasonSelect
        ? isReasonsInvalid || isNotesRequiredInvalid || isNotesTooLong
        : false

    return (
        <Dialog open={open} onClose={cancelDialog}>
            <DialogTitle width="100%" variant="h2">
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6">{title}</Typography>
                    <Stack direction="row" spacing={1}>
                        <IconButton size="small">
                            <ExpandAllIcon sx={{ transform: "rotate(45deg)" }} fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={cancelDialog}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Stack>
                </Stack></DialogTitle>
            <DialogContent>
                {message}
                {hasReasonSelect && (
                    <Autocomplete
                        multiple
                        disableCloseOnSelect
                        autoHighlight
                        options={reasonOptions}
                        value={selectedOptions}
                        getOptionLabel={(option) => option.label}
                        isOptionEqualToValue={(option, selected) =>
                            option.value === selected.value
                        }
                        onChange={(_event, newValue) => {
                            setSelectedReasons(newValue.map((option) => option.value))
                            setReasonsError("")
                        }}
                        renderOption={(optionProps, option, { selected }) => {
                            const { key, ...rest } =
                                optionProps as React.HTMLAttributes<HTMLLIElement> & {
                                    key: string
                                }
                            return (
                                <Box key={option.value} component="li" {...rest}>
                                    <Checkbox
                                        icon={checkboxIcon}
                                        checkedIcon={checkedCheckboxIcon}
                                        style={{ marginRight: 8 }}
                                        checked={selected}
                                    />
                                    {option.label}
                                </Box>
                            )
                        }}
                        renderTags={(value, getTagProps) => (
                            <div
                                style={{
                                    maxHeight: "8rem",
                                    overflowY: "auto",
                                    scrollbarWidth: "thin",
                                    padding: 2,
                                }}
                            >
                                {value.map((option, index) => (
                                    <Chip
                                        {...getTagProps({ index })}
                                        key={option.value}
                                        label={option.label}
                                    />
                                ))}
                            </div>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                onBlur={() => {
                                    if (reasonsRequired && selectedReasons.length === 0) {
                                        setReasonsError("Please select at least one reason.")
                                    }
                                }}
                                placeholder={
                                    selectedOptions.length === 0
                                        ? reasonSelectPlaceholder ?? "Select reason…"
                                        : undefined
                                }
                                slotProps={{
                                    htmlInput: {
                                        ...params.inputProps,
                                        "aria-label": "Reason",
                                    },
                                }}
                                error={!!reasonsError}
                                helperText={<FormErrorPrompt message={reasonsError} />}
                            />
                        )}
                        sx={{ mt: 2, mb: 2 }}
                    />
                )}
                <TextField
                    fullWidth
                    multiline
                    rows={3}
                    variant="outlined"
                    value={reason}
                    placeholder={placeholder}
                    slotProps={{
                        htmlInput: { "aria-label": "Supporting information" },
                    }}
                    sx={{
                        "& .mui-i8rutw-MuiFormHelperText-root": {
                            marginLeft: "0 !important"
                        }
                    }}
                    onChange={(e) => {
                        setReason(e.target.value);
                        setError("");
                    }}
                    error={!!error}
                    helperText={<FormErrorPrompt message={error} />}
                />
            </DialogContent>
            <DialogActions sx={{ padding: "5px 24px 15px" }}>
                {cancelLabel && <Button data-testid="cancelBtn"
                    aria-label="Cancel"
                    variant="outlined"
                    size="small"
                    color="inherit" onClick={cancelDialog}
                    {...cancelProps}>
                    {cancelLabel}
                </Button>}
                {confirmLabel && <Button onClick={handleConfirm}
                    data-testid="confirmBtn"
                    aria-label="Confirm"
                    variant="contained"
                    size="small"
                    color="error"
                    disabled={isConfirmDisabled}
                    {...confirmProps}>
                    {confirmLabel}
                </Button>}
            </DialogActions>
        </Dialog>
    )
}
